import React, { useState, useEffect } from 'react';
import './Gallery.scss';

interface GalleryProps {
  event: any;
}

const Gallery: React.FC<GalleryProps> = ({ event }) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isAutoPlaying, setIsAutoPlaying] = useState(true);
  
  const images = event.images || [];

  const normalizeImageUrl = (value?: string | null) => {
    if (!value) return '';
    if (value.startsWith('http://') || value.startsWith('https://') || value.startsWith('data:')) {
      return value;
    }

    if (value.startsWith('/')) {
      const apiBase = process.env.REACT_APP_API_BASE_URL || '';
      const origin =
        typeof window !== 'undefined' && window.location?.origin ? window.location.origin : '';

      if (apiBase.startsWith('http://') || apiBase.startsWith('https://')) {
        return new URL(value, apiBase).toString();
      }

      if (origin) {
        return `${origin}${value}`;
      }
    }

    return value;
  };

  const getImageSrc = (image: any) => {
    if (!image) return '';
    if (typeof image === 'string') return normalizeImageUrl(image);
    if (typeof image.image === 'string') return normalizeImageUrl(image.image);
    if (image.image?.url) return normalizeImageUrl(image.image.url);
    if (typeof image.image_url === 'string') return normalizeImageUrl(image.image_url);
    if (typeof image.url === 'string') return normalizeImageUrl(image.url);
    return '';
  };

  // Auto-slide functionality
  useEffect(() => {
    if (!isAutoPlaying || images.length <= 1) return;

    const interval = setInterval(() => {
      setCurrentIndex((prevIndex) => (prevIndex + 1) % images.length);
    }, 4000); // Change image every 4 seconds

    return () => clearInterval(interval);
  }, [isAutoPlaying, images.length]);

  if (images.length === 0) {
    return null;
  }

  const goToSlide = (index: number) => {
    setCurrentIndex(index);
    setIsAutoPlaying(false); // Stop auto-play when user manually selects
  };

  const nextSlide = () => {
    setCurrentIndex((prevIndex) => (prevIndex + 1) % images.length);
    setIsAutoPlaying(false);
  };

  const prevSlide = () => {
    setCurrentIndex((prevIndex) => (prevIndex - 1 + images.length) % images.length);
    setIsAutoPlaying(false);
  };

  const toggleAutoPlay = () => {
    setIsAutoPlaying(!isAutoPlaying);
  };

  return (
    <div className="gallery-section">
      <h2 className="section-title">
        <i className="fas fa-images me-2"></i>
        Gallery
      </h2>
      
      <div className="gallery-slider">
        {/* Main Image Display */}
        <div className="main-image-container">
          <img
            src={getImageSrc(images[currentIndex])}
            alt={images[currentIndex]?.caption || `Gallery image ${currentIndex + 1}`}
            className="main-image"
            loading="lazy"
            decoding="async"
          />
          
          {/* Image Caption */}
          {images[currentIndex]?.caption && (
            <div className="image-caption">
              {images[currentIndex].caption}
            </div>
          )}
          
          {/* Navigation Arrows */}
          {images.length > 1 && (
            <>
              <button className="nav-arrow nav-prev" onClick={prevSlide}>
                <i className="fas fa-chevron-left"></i>
              </button>
              <button className="nav-arrow nav-next" onClick={nextSlide}>
                <i className="fas fa-chevron-right"></i>
              </button>
            </>
          )}
          
          {/* Auto-play Toggle */}
          {images.length > 1 && (
            <button className="autoplay-toggle" onClick={toggleAutoPlay}>
              <i className={`fas ${isAutoPlaying ? 'fa-pause' : 'fa-play'}`}></i>
            </button>
          )}
          
          {/* Image Counter */}
          {images.length > 1 && (
            <div className="image-counter">
              {currentIndex + 1} / {images.length}
            </div>
          )}
        </div>
        
        {/* Thumbnail Navigation */}
        {images.length > 1 && (
          <div className="thumbnail-container">
            <div className="thumbnail-scroll">
              {images.map((image: any, index: number) => (
                <div
                  key={image.id || index}
                  className={`thumbnail ${index === currentIndex ? 'active' : ''}`}
                  onClick={() => goToSlide(index)}
                >
                  <img
                    src={getImageSrc(image)}
                    alt={image.caption || `Thumbnail ${index + 1}`}
                    loading="lazy"
                  />
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Gallery;
